import React, {useEffect, useState} from 'react';
import Modal from '../workspace/Modal';
import {Conversion} from '../../domain/types';
import {ConversionSource} from '../../store/selectors/conversions.selectors';
import {controlClass} from '../formStyles';
import {RefreshIcon} from '../Icons';
import {conversionFileName, markerLabel, wordListSummary} from './ConversionRow';

export interface RegenerateSubmission {
  /** Written into the XML as the media reference. Required — a blank one names no file to relink. */
  mediaReference: string;
  fps: number;
}

interface RegenerateConversionModalProps {
  conversion: Conversion | null;
  source: ConversionSource | null;
  isConverting: boolean;
  onCancel: () => void;
  onConfirm: (submission: RegenerateSubmission) => void;
}

/**
 * Re-runs one legacy conversion, and asks for the two things repeating it cannot recover.
 *
 * Everything else is on the record and is reused as-is — the same CSV, the same word list, the same
 * marker mode — so this is a confirmation rather than the full convert dialog. The two exceptions
 * are the point of the exercise: the **media reference** was never recorded (that is why the row is
 * flagged at all), and the **frame rate** was never recorded either. Defaulting the frame rate
 * quietly would trade an XML whose audio needs relinking for one whose cuts land in the wrong
 * place, which is the worse failure of the two because nothing about it looks wrong.
 */
const RegenerateConversionModal: React.FC<RegenerateConversionModalProps> = ({
  conversion,
  source,
  isConverting,
  onCancel,
  onConfirm,
}) => {
  const [mediaReference, setMediaReference] = useState('');
  const [fps, setFps] = useState('29.97');

  useEffect(() => {
    if (!conversion) {
      return;
    }
    // The transcription's own audio reference, which is the name this XML should have carried.
    setMediaReference(source?.mediaReference ?? '');
    setFps('29.97');
  }, [conversion, source]);

  if (!conversion) {
    return null;
  }

  const parsedFps = Number.parseFloat(fps);
  const isFpsValid = Number.isFinite(parsedFps) && parsedFps > 0;
  const trimmedMediaReference = mediaReference.trim();
  const isMediaReferenceValid = trimmedMediaReference.length > 0;
  const canConvert = isFpsValid && isMediaReferenceValid && !isConverting;
  const wordLists = wordListSummary(conversion);

  return (
    <Modal
      isOpen
      title="Convert this again"
      subtitle={
        <>
          <span className="text-gray-400">Replacing: </span>
          <span className="text-gray-200">{conversionFileName(conversion)}</span>
        </>
      }
      onClose={onCancel}
      footer={
        <>
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg px-3 py-1.5 text-sm text-gray-400 hover:text-gray-100
             hover:bg-gray-800 transition-colors cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canConvert}
            onClick={() => onConfirm({mediaReference: trimmedMediaReference, fps: parsedFps})}
            className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm
             font-medium text-white hover:bg-indigo-500 transition-colors
             disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
          >
            <RefreshIcon className={`w-4 h-4 ${isConverting ? 'animate-spin' : ''}`} />
            {isConverting ? 'Converting…' : 'Convert again'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-400">
          This runs the same conversion over again against the same transcript and the same word
          lists, and this time records the audio file the XML points at. The existing XML is left
          alone — the new one arrives at the top of the list.
        </p>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-lg border border-gray-800
         bg-gray-950/40 px-3 py-2.5 text-xs">
          <div className="min-w-0">
            <dt className="text-gray-500">
              {conversion.wordListNames.length > 1 ? 'Word lists' : 'Word list'}
            </dt>
            {/* The names as they were when this ran. A list deleted since is still named here, and
                is also why the repeat can now be refused — see the container's catch. */}
            <dd className="truncate text-gray-200" title={wordLists?.title}>
              {wordLists?.label ?? 'The list this was made with'}
            </dd>
          </div>
          <div className="min-w-0">
            <dt className="text-gray-500">Markers</dt>
            <dd className="truncate text-gray-200">{markerLabel(conversion.markers)}</dd>
          </div>
        </dl>

        <div>
          <label htmlFor="regenerate-audio-path" className="mb-1 block text-xs font-medium text-gray-300">
            Audio file name or path on your machine
          </label>
          <input
            id="regenerate-audio-path"
            type="text"
            value={mediaReference}
            onChange={event => setMediaReference(event.target.value)}
            placeholder="E:\podcast\episode-42.mp3"
            aria-invalid={!isMediaReferenceValid}
            className={`${controlClass} ${isMediaReferenceValid ? '' : 'border-red-500/50'}`}
          />
          {isMediaReferenceValid ? (
            <p className="mt-1 text-xs text-gray-600">
              Prefilled from the transcription this came from. The full path lets your editor relink
              the audio on its own; a bare filename means pointing it at the file yourself after the
              import.
            </p>
          ) : (
            <p className="mt-1 text-xs text-red-400">
              Required. Some video editors cannot relink/offline a clip with no file name.
            </p>
          )}
        </div>

        <div>
          <label htmlFor="regenerate-fps" className="mb-1 block text-xs font-medium text-gray-300">
            Frames per second
          </label>
          <input
            id="regenerate-fps"
            type="text"
            inputMode="decimal"
            value={fps}
            onChange={event => setFps(event.target.value)}
            className={`${controlClass} ${isFpsValid ? '' : 'border-red-500/50'}`}
          />
          {isFpsValid ? (
            <p className="mt-1 text-xs text-gray-600">
              The frame rate of the original conversion was not recorded, so this is the default
              rather than what you used. Set it to your sequence's rate — at the wrong one the cuts
              land in the wrong place.
            </p>
          ) : (
            <p className="mt-1 text-xs text-red-400">Must be a number above 0.</p>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default RegenerateConversionModal;
